// uiarc: alert / toast / empty-state ortak parçaları (https://uiarc.dev/r/alert.json): metin değişimi (`Swap`) ve
// içeriğin yüksekliğini yayla izleyen çerçeve (`HeightFrame`). Çıkan metin ekran okuyucudan gizlenir; çerçeve yalnız
// hareket ederken kırpar, böylece dinlenirken odak halkaları görünür kalır. `prefers-reduced-motion` altında yalnız solma.
import {
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  type AnimationPlaybackControls,
  type HTMLMotionProps,
} from 'motion/react'
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const { spring } = motionTokens

/** Çıkan kopyalar solarken ekran okuyucudan gizlenir; canlı bölge yalnız güncel metni okur. */
function Swap({ className, ...props }: HTMLMotionProps<'span'>) {
  const present = useIsPresent()
  return (
    <motion.span
      {...props}
      className={className}
      aria-hidden={present ? props['aria-hidden'] : true}
    />
  )
}

/**
 * İçerik yüksekliğini izler. `morphKey` değişince yükseklik eski ölçüden yenisine yayla gider, sonra `auto`ya döner;
 * pasif yeniden akışlar (pencere boyutu, yazı tipi) anında izlenir.
 */
function HeightFrame({
  className,
  contentClassName,
  reduce,
  morphKey,
  children,
}: {
  className?: string
  contentClassName?: string
  reduce: boolean | null
  morphKey: string
  children: ReactNode
}) {
  const frame = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const height = useMotionValue<number | 'auto'>('auto')
  const changedAt = useRef(0)
  useLayoutEffect(() => {
    changedAt.current = performance.now()
  }, [morphKey])
  useEffect(() => {
    const node = content.current
    if (!node || typeof ResizeObserver === 'undefined') return
    let last: number | undefined
    let controls: AnimationPlaybackControls | undefined
    const settle = () => {
      height.jump('auto')
      if (frame.current) Object.assign(frame.current.style, { overflow: '', height: 'auto' })
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight
      const current = height.get()
      const from = typeof current === 'number' ? current : last
      last = next
      controls?.stop()
      if (
        reduce ||
        from === undefined ||
        from === next ||
        performance.now() - changedAt.current > 120
      )
        return settle()
      // Eski yüksekliği boyamadan önce sabitle, sonra yenisine yayla git.
      if (frame.current)
        Object.assign(frame.current.style, { overflow: 'hidden', height: `${from}px` })
      controls = animate(height, [from, next], { ...spring.smooth, onComplete: settle })
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      controls?.stop()
    }
  }, [height, reduce])
  return (
    <motion.div ref={frame} className={className} style={{ height }}>
      <div ref={content} className={cn('relative', contentClassName)}>
        {children}
      </div>
    </motion.div>
  )
}

export { HeightFrame, Swap }
